import type { UserRole } from "@/dashboard/features/userManagement/lib/schema";
import { USER_ROLE_HIERARCHY } from "@/dashboard/features/userManagement/lib/utils";
import { ALL_TENANTS_ID } from "@/dashboard/lib/auth/constants";
import { isReadonlySafePost } from "@/dashboard/lib/auth/readonly-safe-routes";
import type { User } from "@/dashboard/types/user";

/**
 * Get the user's role for a specific tenant.
 * Super admins are treated as admins for all tenants.
 */
export function getRoleForTenant(user: User, tenantId: string): UserRole | null {
  if (user.superAdmin) {
    return "admin";
  }

  // Call-taker users have member-level access to all tenants
  if (user.neticRole === "call_taker") {
    return "member";
  }

  return user.tenantPermissions[tenantId]?.role ?? null;
}

/**
 * Filter tenants to those the user can switch to. Super admins and call takers can reach all of them.
 */
export function getAccessibleTenants<T extends { id: string }>(
  user: Pick<User, "superAdmin" | "neticRole" | "tenantPermissions">,
  tenants: T[]
): T[] {
  if (user.superAdmin || user.neticRole === "call_taker") {
    return tenants;
  }
  return tenants.filter(tenant => user.tenantPermissions[tenant.id] !== undefined);
}

/**
 * Check if user has at least the required role for a specific tenant.
 * Uses role hierarchy: member < manager < admin (netic_view / netic_ds == admin level for reads)
 */
export function hasRoleForTenant(user: User, tenantId: string, requiredRole: UserRole): boolean {
  const userRole = getRoleForTenant(user, tenantId);

  if (!userRole) {
    return false;
  }

  return USER_ROLE_HIERARCHY[userRole] >= USER_ROLE_HIERARCHY[requiredRole];
}

/**
 * Check if the user only has non_dashboard_user roles (e.g. Greenhouse-only).
 * Super admins are never considered non-dashboard users.
 */
export function isNonDashboardUser(user: Pick<User, "superAdmin" | "tenantPermissions">): boolean {
  if (user.superAdmin) return false;
  const permissions = Object.values(user.tenantPermissions);
  return permissions.length > 0 && permissions.every(p => p.role === "non_dashboard_user");
}

/**
 * Check if the user only has analytics_viewer roles.
 * Super admins are never considered analytics-only users.
 */
export function isAnalyticsViewer(user: Pick<User, "superAdmin" | "tenantPermissions">): boolean {
  if (user.superAdmin) {
    return false;
  }
  const permissions = Object.values(user.tenantPermissions);
  return permissions.length > 0 && permissions.every(p => p.role === "analytics_viewer");
}

export function hasRoleForAnyTenant(user: User, requiredRole: UserRole): boolean {
  if (user.superAdmin) {
    return true;
  }

  const requiredLevel = USER_ROLE_HIERARCHY[requiredRole];

  // Check if user has the required role in any tenant
  return Object.values(user.tenantPermissions).some(
    perm => USER_ROLE_HIERARCHY[perm.role] >= requiredLevel
  );
}

export function canManageTechnicians(user: User, tenantId: string): boolean {
  return tenantId === ALL_TENANTS_ID
    ? hasRoleForAnyTenant(user, "manager")
    : hasRoleForTenant(user, tenantId, "manager");
}

/** True when every tenant permission is the Netic view-only special role. */
export function isNeticViewOnly(user: Pick<User, "tenantPermissions">): boolean {
  const permissions = Object.values(user.tenantPermissions);
  return permissions.length > 0 && permissions.every(p => p.role === "netic_view");
}

/** True when every tenant permission is the Netic DS limited-edit special role. */
export function isNeticDsLimited(user: Pick<User, "tenantPermissions">): boolean {
  const permissions = Object.values(user.tenantPermissions);
  return permissions.length > 0 && permissions.every(p => p.role === "netic_ds");
}

/**
 * Campaign launch / pause / archive. Netic view + DS specials cannot change campaign active state.
 */
export function canLaunchOrArchiveCampaign(user: User, tenantId: string): boolean {
  if (isNeticViewOnly(user) || isNeticDsLimited(user)) {
    return false;
  }
  return hasRoleForTenant(user, tenantId, "manager");
}

/**
 * Turning off the promotional opt-in gate requires a real tenant admin (or super admin),
 * not a Netic special account that only inherits admin-level read access.
 */
export function canDisableOptInGate(user: User, tenantId: string): boolean {
  if (user.superAdmin) {
    return true;
  }
  return user.tenantPermissions[tenantId]?.role === "admin";
}

/**
 * Paths that netic_ds may mutate. Everything else is read-only for that role.
 * Session / tenant-select are always allowed so auth plumbing keeps working.
 */
const NETIC_DS_WRITE_SAFE_PATHS = new Set<string>([
  "/api/dashboard/session",
  "/api/dashboard/tenant/select",
  "/api/dashboard/realtime/ticket",
]);

/**
 * Returns true if a netic_ds user is allowed to perform this non-GET request.
 * View-only enforcement is handled separately (cookie + proxy).
 */
export function isNeticDsWriteAllowed(method: string, pathname: string): boolean {
  if (method === "GET" || method === "HEAD" || method === "OPTIONS") {
    return true;
  }

  if (NETIC_DS_WRITE_SAFE_PATHS.has(pathname) || isReadonlySafePost(method, pathname)) {
    return true;
  }

  // Draft campaign create / update (language + config)
  if (
    pathname === "/api/dashboard/marketing/beta-campaigns/campaigns" &&
    (method === "POST" || method === "PUT")
  ) {
    return true;
  }

  // Copy creates a new draft
  if (
    /^\/api\/dashboard\/marketing\/beta-campaigns\/campaigns\/[^/]+\/copy$/.test(pathname) &&
    method === "POST"
  ) {
    return true;
  }

  // Audience create / update / delete / refresh / preview / potential-reach / live-options / export
  if (pathname.startsWith("/api/dashboard/marketing/audiences")) {
    return true;
  }

  // Remove audience member
  if (pathname.startsWith("/api/dashboard/marketing/audience-members")) {
    return true;
  }

  // Instantiate template as draft campaign
  if (pathname === "/api/dashboard/marketing/campaign-templates/instantiate" && method === "POST") {
    return true;
  }

  // Consent attestations other than opt-in-gate-off (gate-off denied in the route itself)
  if (pathname === "/api/dashboard/marketing/campaigns/consent-attestations" && method === "POST") {
    return true;
  }

  return false;
}

/**
 * Returns an error message when a Netic special account is not allowed to write,
 * or null when the write is permitted (including non-special users).
 * netic_view writes are primarily blocked in request-proxy via cookie; this is a belt-and-suspenders check.
 */
export function getNeticSpecialWriteDenial(
  user: Pick<User, "tenantPermissions">,
  method: string,
  pathname: string
): string | null {
  if (method === "GET" || method === "HEAD" || method === "OPTIONS") {
    return null;
  }

  if (isNeticViewOnly(user)) {
    if (
      pathname === "/api/dashboard/session" ||
      pathname === "/api/dashboard/tenant/select" ||
      pathname === "/api/dashboard/realtime/ticket" ||
      isReadonlySafePost(method, pathname)
    ) {
      return null;
    }
    return "View-only sessions cannot make changes";
  }

  if (isNeticDsLimited(user) && !isNeticDsWriteAllowed(method, pathname)) {
    return "DS limited-edit accounts cannot perform this action";
  }

  return null;
}
